import { useId, useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { useApplicationRemarks, useRemarkActions } from '../api/remarks-api';
import { RemarkComposer } from './RemarkComposer';
import { RemarkThread } from './RemarkThread';
import styles from './remarks.module.css';

// F23.13 (ADR 0049): RNK-02's Remarks — the box to add one, then the person's thread, newest first.

interface RemarksPanelProps {
  readonly applicationId: string;
  readonly jobId: string;
  readonly name: string;
}

export function RemarksPanel({ applicationId, jobId, name }: RemarksPanelProps): ReactNode {
  const titleId = useId();
  const remarks = useApplicationRemarks(applicationId);
  const actions = useRemarkActions(jobId);
  const [editingId, setEditingId] = useState<string | null>(null);
  // A new key empties the box: the remark is already in the thread (§5a rule 1).
  const [composerKey, setComposerKey] = useState(0);
  const data = remarks.data;

  return (
    <section className={styles.panel} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.panelTitle}>
        Remarks
        {data && data.remarks.length > 0 && <span className={styles.cellCount}>{data.remarks.length}</span>}
      </h3>
      {remarks.isPending && <SkeletonLines count={3} />}
      {remarks.error && !data && (
        <p className={styles.empty}>
          The remarks couldn't be loaded.{' '}
          <Button size="sm" variant="ghost" onClick={() => void remarks.refetch()}>
            Try again
          </Button>
        </p>
      )}
      {data?.mayAdd && (
        <RemarkComposer
          key={composerKey}
          heading={`Add a remark on ${name}`}
          mentionable={data.mentionable}
          onSubmit={(draft) => {
            setComposerKey((key) => key + 1);
            void actions.add(applicationId, draft);
          }}
          onCancel={() => setComposerKey((key) => key + 1)}
        />
      )}
      {data && (
        <RemarkThread
          remarks={data.remarks}
          actions={
            data.mayAdd
              ? {
                  mentionable: data.mentionable,
                  editingId,
                  onEditStart: (remark) => setEditingId(remark.id),
                  onEditCancel: () => setEditingId(null),
                  onEditSave: (remark, draft) => {
                    setEditingId(null);
                    void actions.edit(remark, draft);
                  },
                  onRemove: (remark) => void actions.remove(remark),
                }
              : undefined
          }
        />
      )}
    </section>
  );
}
